import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { AclPermissionsRole } from '@eventmanager/types';
import { useAppSelector } from '../../hooks';
import { currentUser } from '../../selectors';
import { c, Api } from '../../utils';
import { Clickable, ClickableVariant } from '../Clickable';
import { PlanDetails } from '../PlanDetails';
import { PlanHeader } from '../PlanHeader';
import { PlanPriceLabel } from './PlanPriceLabel';
import { trackContactSales } from '../../libs/trackingLib';
import { PaymentType } from '../../../types/src';

import classes from './PricingPlan.module.scss';
import { CircularProgress } from '@material-ui/core';

type Props = {
  plan: { role: AclPermissionsRole; monthlyFee: number };
};

export function PricingPlan({ plan }: Props) {
  const user = useAppSelector(currentUser);
  const [, setLocation] = useLocation();

  const [isLoading, setLoading] = useState(false);

  // [2026-03-07] Handle user subscriptions to Starter, Pro, and Enterprise plans.
  const handleCheckoutPlan = async (selectedPlanName: AclPermissionsRole) => {
    if (!user) return;
    setLoading(true);
    try {
      // Parameters used to request a Checkout Portal session
      const paymentIntentParams = {
        paymentType: 'subscribe' as PaymentType,
        planName: selectedPlanName,
        userId: user.userId,
      };

      // Request the Checkout Portal session URL from the API
      const response = await Api.newPaymentIntent(paymentIntentParams);

      if (!response?.url) {
        // [2026-05-18] The backend already generates an error and notifies
        // the technical team when Stripe omits the URL, so we don't notify
        // them again here; instead, we simply redirect the user to the error page.
        setLoading(false);
        setLocation(
          `/billing-error?reason=checkout_failed&plan=${encodeURIComponent(
            selectedPlanName,
          )}`,
        );
        return;
      }

      setLoading(false);

      // Redirect the user to the Stripe Checkout Portal
      window.location.href = response.url;

      trackContactSales();
    } catch (error) {
      // [2026-05-18] Redirect the user to the error page and log the error.
      console.error('Error creating payment intent:', error);
      setLoading(false);
      setLocation(
        `/billing-error?reason=checkout_failed&plan=${encodeURIComponent(
          selectedPlanName,
        )}`,
      );
    }
  };

  return (
    <div
      className={c(classes.PricingPlans, 'flex flex-col gap-1 p2')}
      key={plan.role}
    >
      <PlanHeader planName={plan.role} />
      <PlanPriceLabel monthlyFee={plan.monthlyFee} />
      <Clickable
        variant={ClickableVariant.BUTTON_PRIMARY}
        onClick={() => handleCheckoutPlan(plan.role)}
        className="bg-color-gradient"
      >
        {/* Add loading spinner while fetching Stripe Checkout Portal URL */}
        {isLoading ? (
          <div className="flex justify-center">
            Redirecting....
            <CircularProgress
              style={{
                color: 'white',
                width: 20,
                height: 20,
                marginLeft: '10px',
              }}
            />
          </div>
        ) : (
          'Try now'
        )}
      </Clickable>
      <PlanDetails role={plan.role} />
    </div>
  );
}
